import { useState, useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import ToolbarFilterDropdown from '../components/ToolbarFilterDropdown';
import './Customers.css';
import './MsaList.css';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

export default function MsaList() {
  const { canCreate } = useAuth();
  const [msas, setMsas] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pageSizeOpen, setPageSizeOpen] = useState(false);
  const pageSizeDdRef = useRef(null);
  const [filterMenuOpen, setFilterMenuOpen] = useState(null);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    api
      .get('/msa')
      .then(rows => mounted && setMsas(rows || []))
      .catch(() => mounted && setMsas([]))
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!pageSizeOpen) return undefined;
    const close = () => setPageSizeOpen(false);
    const onDoc = e => {
      if (pageSizeDdRef.current && !pageSizeDdRef.current.contains(e.target)) close();
    };
    const onKey = e => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [pageSizeOpen]);

  const statusFilterOptions = useMemo(() => {
    const set = new Set(msas.map(m => m.status).filter(Boolean));
    const sorted = [...set].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
    return [{ value: '', label: 'All Statuses' }, ...sorted.map(s => ({ value: s, label: s }))];
  }, [msas]);

  const filtered = useMemo(
    () =>
      msas.filter(m => {
        if (statusFilter && m.status !== statusFilter) return false;
        if (!search) return true;
        const s = search.toLowerCase();
        return `${m.msa_id} ${m.msa_ref || ''} ${m.client_name || ''}`.toLowerCase().includes(s);
      }),
    [msas, search, statusFilter]
  );

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter, pageSize]);

  useEffect(() => {
    const tp = Math.max(1, Math.ceil(filtered.length / pageSize));
    setPage(p => Math.min(p, tp));
  }, [filtered.length, pageSize]);

  const totalFiltered = filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / pageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const offset = (safePage - 1) * pageSize;
  const pageRows = filtered.slice(offset, offset + pageSize);
  const rangeStart = totalFiltered === 0 ? 0 : offset + 1;
  const rangeEnd = totalFiltered === 0 ? 0 : Math.min(offset + pageSize, totalFiltered);

  const hasFilters = !!(search || statusFilter);
  const clearFilters = () => {
    setSearch('');
    setStatusFilter('');
  };

  return (
    <>
      <div className="page-header">
        <h1>MSA Agreements</h1>
      </div>
      <div className="page-content msa-list-page">
        <div className="customers-top">
          <div className="customers-stats" aria-label="MSA agreement counts">
            <div className="customers-stat">
              <div className="customers-stat-label">In list</div>
              <div className="customers-stat-value">{loading ? '—' : msas.length.toLocaleString()}</div>
            </div>
            <div className="customers-stat">
              <div className="customers-stat-label">Match filters</div>
              <div className="customers-stat-value">{loading ? '—' : filtered.length.toLocaleString()}</div>
            </div>
          </div>
          <div className="customers-top-right">
            {hasFilters && (
              <button type="button" className="btn btn-outline btn-sm" onClick={clearFilters}>
                Clear filters
              </button>
            )}
            {canCreate('msa') && (
              <Link to="/msa/new" className="btn btn-primary btn-sm customers-new-btn">
                + New MSA
              </Link>
            )}
          </div>
        </div>

        {msas.length > 0 && filtered.length < msas.length && (
          <div className="customers-filter-hint" role="status">
            <span>
              Filters narrow the table to <strong>{filtered.length}</strong> of <strong>{msas.length}</strong> agreement
              {msas.length === 1 ? '' : 's'} in your current list.
            </span>
            <button type="button" className="btn btn-outline btn-sm" onClick={clearFilters}>
              Clear filters
            </button>
          </div>
        )}

        <div className="customers-toolbar">
          <div className="customers-toolbar-left">
            <div className="customers-search">
              <span className="customers-search-icon" aria-hidden="true">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M10.5 18a7.5 7.5 0 1 1 0-15 7.5 7.5 0 0 1 0 15Z"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                  <path d="M16.3 16.3 21 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </span>
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search by MSA ID, reference, or client…"
                aria-label="Search MSA agreements"
              />
            </div>
            <div className="customers-filters">
              <ToolbarFilterDropdown
                id="msa-filter-status"
                labelText="Status"
                ariaLabel="Filter by status"
                value={statusFilter}
                onChange={setStatusFilter}
                placeholder="All Statuses"
                options={statusFilterOptions}
                menuKey="msa-status"
                openMenu={filterMenuOpen}
                setOpenMenu={setFilterMenuOpen}
              />
            </div>
          </div>
        </div>

        <div className="card">
          <div className="msa-list-table">
            <table>
              <thead>
                <tr>
                  <th>MSA ID</th>
                  <th>Ref</th>
                  <th>Client</th>
                  <th>Effective</th>
                  <th>End date</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map(m => (
                  <tr key={m.msa_id}>
                    <td>
                      <Link to={`/msa/${m.msa_id}`} className="link">
                        {m.msa_id}
                      </Link>
                    </td>
                    <td title={m.msa_ref || ''}>{m.msa_ref || '—'}</td>
                    <td title={m.client_name}>{m.client_name}</td>
                    <td>{m.effective_date}</td>
                    <td>{m.end_date}</td>
                    <td>
                      <span className={`badge badge-${m.status?.toLowerCase()}`}>{m.status}</span>
                    </td>
                  </tr>
                ))}
                {!filtered.length && (
                  <tr>
                    <td colSpan="6" className="msa-list-empty">
                      {loading ? 'Loading MSA agreements…' : 'No MSA agreements match the filters.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {!loading && totalFiltered > 0 && (
            <div className="customers-pagination" role="navigation" aria-label="Table pagination">
              <div className="customers-pagination-summary">
                <span className="customers-pagination-summary-label">Showing</span>
                <span className="customers-pagination-summary-range">
                  <strong className="tabular-nums">{rangeStart}</strong>
                  <span className="customers-pagination-summary-dash">–</span>
                  <strong className="tabular-nums">{rangeEnd}</strong>
                  <span className="customers-pagination-summary-of">of</span>
                  <strong className="tabular-nums">{totalFiltered.toLocaleString()}</strong>
                  <span className="customers-pagination-summary-unit">rows</span>
                </span>
              </div>
              <div className="customers-pagination-controls">
                <div className="customers-pagination-page-size" ref={pageSizeDdRef}>
                  <span className="customers-pagination-label" id="msa-page-size-label">
                    Rows per page
                  </span>
                  <div className="customers-page-size-dd-inner">
                    <button
                      type="button"
                      className={`customers-page-size-trigger ${pageSizeOpen ? 'customers-page-size-trigger-open' : ''}`}
                      aria-haspopup="listbox"
                      aria-expanded={pageSizeOpen}
                      aria-label={`Rows per page, current value ${pageSize}`}
                      onClick={() => setPageSizeOpen(o => !o)}
                    >
                      <span>{pageSize}</span>
                      <span className="customers-page-size-chevron" aria-hidden>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M6 9l6 6 6-6" />
                        </svg>
                      </span>
                    </button>
                    {pageSizeOpen && (
                      <ul className="customers-page-size-menu" role="listbox" aria-labelledby="msa-page-size-label">
                        {PAGE_SIZE_OPTIONS.map(n => (
                          <li
                            key={n}
                            role="option"
                            aria-selected={pageSize === n}
                            className={
                              pageSize === n
                                ? 'customers-page-size-option customers-page-size-option-active'
                                : 'customers-page-size-option'
                            }
                            onMouseDown={e => {
                              e.preventDefault();
                              setPageSize(n);
                              setPageSizeOpen(false);
                            }}
                          >
                            {n}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
                <div className="customers-pagination-pager" role="group" aria-label="Page navigation">
                  <button
                    type="button"
                    className="customers-pagination-step"
                    disabled={safePage <= 1}
                    onClick={() => setPage(1)}
                    aria-label="First page"
                    title="First page"
                  >
                    <span className="customers-pagination-step-icon" aria-hidden>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M11 19l-7-7 7-7" />
                        <path d="M18 19l-7-7 7-7" />
                      </svg>
                    </span>
                    <span>First</span>
                  </button>
                  <button
                    type="button"
                    className="customers-pagination-step"
                    disabled={safePage <= 1}
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    aria-label="Previous page"
                    title="Previous page"
                  >
                    <span className="customers-pagination-step-icon" aria-hidden>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M15 18l-6-6 6-6" />
                      </svg>
                    </span>
                    <span>Prev</span>
                  </button>
                  <div className="customers-pagination-page-chip">
                    <span className="customers-pagination-page-label">Page</span>
                    <span className="customers-pagination-page-current tabular-nums">{safePage}</span>
                    <span className="customers-pagination-page-sep">/</span>
                    <span className="customers-pagination-page-total tabular-nums">{totalPages}</span>
                  </div>
                  <button
                    type="button"
                    className="customers-pagination-step"
                    disabled={safePage >= totalPages}
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    aria-label="Next page"
                    title="Next page"
                  >
                    <span>Next</span>
                    <span className="customers-pagination-step-icon" aria-hidden>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M9 18l6-6-6-6" />
                      </svg>
                    </span>
                  </button>
                  <button
                    type="button"
                    className="customers-pagination-step"
                    disabled={safePage >= totalPages}
                    onClick={() => setPage(totalPages)}
                    aria-label="Last page"
                    title="Last page"
                  >
                    <span>Last</span>
                    <span className="customers-pagination-step-icon" aria-hidden>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M13 5l7 7-7 7" />
                        <path d="M6 5l7 7-7 7" />
                      </svg>
                    </span>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
